import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

// Componente nuevo (no es una copia directa de petrol-ng: la librería no
// tiene un botón de ícono reutilizable aparte). Sigue el mismo estilo visual
// que usan los botones cuadrados del componente Pagination de petrol-ng
// (cuadrado, sin bordes redondeados, fondo blanco con borde que se resalta
// en el color primario al pasar el mouse), pero como pieza aparte y usando
// primeicons en vez de la fuente "Material Symbols" que usa petrol-ng.
// Ver plan/plan-9-boton_icono.md.

export type IconButtonType = 'button' | 'submit' | 'reset';

/**
 * `variant`: `'square'` (por defecto) es el estilo original — cuadrado, sin
 * bordes redondeados, pensado para apoyarse sobre fondo blanco (paginación,
 * zoom, wizards). `'flat'` es una variante sin borde/fondo propio, pensada
 * para íconos que ya viven sobre una barra con su propio fondo (ej. el
 * topbar) — ver plan/plan-27-topbar_parity_oldelval.md.
 */
export type IconButtonVariant = 'square' | 'flat';

const ROOT_BASE =
  'flex h-8 w-8 items-center justify-center border border-slate-300 bg-white text-slate-500 outline-none transition-colors ' +
  'hover:border-primary hover:text-primary disabled:pointer-events-none disabled:opacity-40 ' +
  'dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-primary-light dark:hover:text-primary-light';

const ROOT_FLAT =
  'flex h-9 w-9 items-center justify-center rounded-md text-slate-500 outline-none transition-colors ' +
  'hover:bg-primary-light/40 hover:text-primary disabled:pointer-events-none disabled:opacity-40';

/**
 * Botón cuadrado para acciones representadas solo por un ícono, sin texto
 * visible (ej. flechas de paginación, zoom, avanzar/retroceder paso). Al no
 * tener texto, `ariaLabel` es obligatorio para que sea accesible.
 */
@Component({
  selector: 'petrol-icon-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './icon-button.component.html',
})
export class PetrolIconButtonComponent {
  readonly icon = input.required<string>();
  readonly ariaLabel = input.required<string>();
  readonly type = input<IconButtonType>('button');
  readonly disabled = input(false);
  readonly variant = input<IconButtonVariant>('square');
  /**
   * Id opcional que se aplica al `<button>` real de adentro (no al host
   * `<petrol-icon-button>`). Necesario para casos que dependen de
   * `document.getElementById(...)` para hacer `.focus()`/`.click()` por
   * código (ej. atajo de teclado Enter, pasos de un tour guiado).
   */
  readonly buttonId = input<string | null>(null);

  protected readonly rootClasses = computed(() =>
    this.variant() === 'flat' ? ROOT_FLAT : ROOT_BASE,
  );
}
